import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, CreditCard, Banknote, Loader2, Plus, UtensilsCrossed, Truck, Table2, ShoppingBag, Gift, Tag, Clock, AlertTriangle, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";
const TABLE_QR_KEY = "app_qr_table";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, getTotal, clearCart } = useCart();
  const { user } = useAuth();

  // Restaurant status
  const [restaurantStatus, setRestaurantStatus] = useState({ is_open: true, message: "" });
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  // Branches
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);

  // Check for QR table first, then session storage
  const qrTable = localStorage.getItem(TABLE_QR_KEY);
  const [orderType, setOrderType] = useState(() => {
    if (qrTable) return "local"; // If from QR, default to local
    return sessionStorage.getItem("orderType") || "delivery";
  });
  const [tableNumber, setTableNumber] = useState(() => {
    if (qrTable) return qrTable; // Use QR table number
    return sessionStorage.getItem("tableNumber") || "";
  });
  const [isFromQR, setIsFromQR] = useState(!!qrTable);

  // Pickup orders behave like local orders but customer picks up
  const isPickupOrder = orderType === "pickup";
  const isLocalOrder = orderType === "local" || isPickupOrder;

  const [config, setConfig] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  // Default to cash for local/pickup orders (paid at counter), card for delivery
  const [paymentMethod, setPaymentMethod] = useState(
    orderType === "delivery" ? "card" : "cash"
  );
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: "Casa",
    street: "",
    city: "Almería",
    postal_code: "",
  });

  // Discount state
  const [discountInfo, setDiscountInfo] = useState(null);
  const [isLoadingDiscount, setIsLoadingDiscount] = useState(false);

  useEffect(() => {
    if (searchParams.get("cancelled") === "true") {
      toast.error("Pago cancelado");
    }
  }, [searchParams]);

  // Check restaurant status (with branch-specific check)
  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Get selected branch from session storage
        const savedBranch = sessionStorage.getItem("selectedBranch");
        let branchId = null;
        
        if (savedBranch) {
          try {
            const parsed = JSON.parse(savedBranch);
            branchId = parsed.branch_id;
            console.log("🏪 [CheckoutPage] Checking status for branch:", parsed.name);
          } catch (e) {
            console.error("Error parsing saved branch:", e);
          }
        }
        
        // Call API with branch_id if available
        const url = branchId 
          ? `${API}/restaurant/status?branch_id=${branchId}`
          : `${API}/restaurant/status`;
          
        const response = await axios.get(url);
        console.log("🏪 [CheckoutPage] Restaurant status response:", response.data);
        setRestaurantStatus(response.data);
      } catch (error) {
        console.error("❌ [CheckoutPage] Error checking restaurant status:", error);
      } finally {
        setIsCheckingStatus(false);
      }
    };
    checkStatus();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, addressRes, branchesRes] = await Promise.all([
          axios.get(`${API}/config`),
          axios.get(`${API}/addresses`, { withCredentials: true }),
          axios.get(`${API}/public/branches`)
        ]);
        setConfig(configRes.data);
        setAddresses(addressRes.data);
        if (addressRes.data.length > 0) {
          setSelectedAddress(addressRes.data.find((a) => a.is_default) || addressRes.data[0]);
        }

        // Handle branches (API already filters by active)
        setBranches(branchesRes.data);
        
        console.log("🔍 [CheckoutPage] Sucursales cargadas:", branchesRes.data.length);
        console.log("📍 [CheckoutPage] Sucursales:", branchesRes.data);
        
        // Check if branch was already selected in OrderTypePage
        const savedBranch = sessionStorage.getItem("selectedBranch");
        if (savedBranch) {
          try {
            const parsed = JSON.parse(savedBranch);
            setSelectedBranch(parsed);
            console.log("✅ [CheckoutPage] Sucursal recuperada de session:", parsed.name);
          } catch (e) {
            console.error("❌ [CheckoutPage] Error parsing saved branch:", e);
          }
        } else if (branchesRes.data.length === 1) {
          // Auto-select if only one branch
          setSelectedBranch(branchesRes.data[0]);
          console.log("✅ [CheckoutPage] Auto-seleccionada única sucursal:", branchesRes.data[0].name);
        } else {
          console.log("⚠️ [CheckoutPage] Múltiples sucursales, usuario debe elegir");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoadingBranches(false);
      }
    };

    fetchData();
  }, []);

  // Fetch discount preview when items or order type changes
  useEffect(() => {
    const fetchDiscountPreview = async () => {
      if (items.length === 0) {
        setDiscountInfo(null);
        return;
      }

      setIsLoadingDiscount(true);
      try {
        const response = await axios.post(`${API}/orders/preview`, {
          items: items.map(item => ({
            item_id: item.item_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            extras: item.extras || [],
            notes: item.notes || null
          })),
          order_type: orderType
        });
        setDiscountInfo(response.data);
      } catch (error) {
        console.error("Error fetching discount preview:", error);
        setDiscountInfo(null);
      } finally {
        setIsLoadingDiscount(false);
      }
    };

    fetchDiscountPreview();
  }, [items, orderType]);

  // Use discount info if available, otherwise calculate locally
  const subtotal = discountInfo?.subtotal ?? getTotal();
  const taxRate = config?.tax_rate || 0.10;
  const tax = discountInfo?.tax ?? (subtotal * taxRate);
  const originalDeliveryFee = isLocalOrder ? 0 : (config?.delivery_fee || 3.50);
  const deliveryFee = discountInfo?.delivery_fee ?? originalDeliveryFee;
  const discount = discountInfo?.discount || 0;
  const discountDescriptions = discountInfo?.discount_descriptions || [];
  const savings = discountInfo?.savings || 0;
  const hasDiscounts = discountInfo?.has_discounts || false;
  const total = discountInfo?.total ?? (subtotal + tax + deliveryFee);

  // Geocode address function using Nominatim (OpenStreetMap)
  const geocodeAddress = async (street, city, postal_code) => {
    try {
      const query = `${street}, ${postal_code} ${city}, Spain`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=es`,
        {
          headers: {
            'User-Agent': 'SaaS-POS/1.0'
          }
        }
      );
      const data = await response.json();

      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }

      console.warn("No se encontraron coordenadas para:", query);
      return null;
    } catch (error) {
      console.error("Error geocoding address:", error);
      return null;
    }
  };

  const handleSaveAddress = async () => {
    if (!newAddress.street || !newAddress.postal_code) {
      toast.error("Completa la dirección");
      return;
    }

    try {
      // Geocodificar dirección automáticamente
      toast.info("Obteniendo coordenadas...");
      const coordinates = await geocodeAddress(newAddress.street, newAddress.city, newAddress.postal_code);

      const addressData = {
        ...newAddress,
        lat: coordinates?.lat || null,
        lng: coordinates?.lng || null,
        is_default: addresses.length === 0
      };

      if (coordinates) {
        console.log("✓ Coordenadas obtenidas:", coordinates);
      } else {
        console.warn("⚠️ No se pudieron obtener coordenadas GPS, guardando sin ellas");
      }

      const response = await axios.post(
        `${API}/addresses`,
        addressData,
        { withCredentials: true }
      );
      setAddresses([...addresses, response.data]);
      setSelectedAddress(response.data);
      setShowNewAddress(false);
      setNewAddress({ label: "Casa", street: "", city: "Almería", postal_code: "" });
      toast.success("Dirección guardada con GPS");
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Error al guardar dirección");
    }
  };

  const handlePlaceOrder = async () => {
    // Re-check restaurant status before placing order
    try {
      const statusRes = await axios.get(`${API}/restaurant/status`);
      if (!statusRes.data.is_open) {
        setRestaurantStatus(statusRes.data);
        toast.error("El restaurante ha cerrado. No se puede procesar el pedido.");
        return;
      }
    } catch (error) {
      console.error("Error checking status:", error);
    }

    if (!selectedBranch && branches.length > 0) {
      toast.error("Por favor selecciona una sucursal");
      return;
    }

    // Validate delivery address for delivery orders
    if (!isLocalOrder && !selectedAddress && !showNewAddress) {
      toast.error("Selecciona una dirección de entrega");
      return;
    }

    const addressToUse = selectedAddress || newAddress;

    if (!isLocalOrder && (!addressToUse.street || !addressToUse.postal_code)) {
      toast.error("Completa la dirección de entrega");
      return;
    }

    setIsLoading(true);

    try {
      // Create order
      const orderPayload = {
        items: items.map((item) => ({
          item_id: item.item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          extras: item.extras || [],
          notes: item.notes,
        })),
        order_type: orderType,
        payment_method: paymentMethod,
        notes,
        branch_id: selectedBranch?.branch_id,
      };

      // Add address for delivery or table number for local
      if (isLocalOrder) {
        if (tableNumber) {
          orderPayload.table_number = tableNumber;
        }
      } else {
        orderPayload.address = addressToUse;
      }

      console.log("Creating order with payload:", orderPayload);
      console.log("📝 Payment method being sent:", paymentMethod, "| Order type:", orderType);

      // ... code ...
      const orderResponse = await axios.post(
        `${API}/orders`,
        orderPayload,
        { withCredentials: true }
      );

      const order = orderResponse.data;
      console.log("Order created successfully:", order);

      // Clear cart immediately as order is created
      clearCart();
      sessionStorage.removeItem("orderType");
      sessionStorage.removeItem("tableNumber");
      // Do NOT clear QR table, so user stays "seated"
      // localStorage.removeItem(TABLE_QR_KEY);

      // LOCAL or PICKUP orders
      if (isLocalOrder) {
        const successMessage = paymentMethod === "card" 
          ? "¡Pago recibido! Tu pedido está siendo preparado" 
          : "¡Pedido confirmado! Paga en caja al recoger";
        toast.success(successMessage, { duration: 4000 });
        setIsLoading(false);
        navigate(`/order/${order.order_id}`, { replace: true });
        return;
      }

      // DELIVERY orders
      if (paymentMethod === "card") {
        try {
          // Create Stripe checkout session
          const checkoutResponse = await axios.post(
            `${API}/payments/checkout`,
            {
              order_id: order.order_id,
              origin_url: window.location.origin,
            },
            { withCredentials: true }
          );

          // Redirect to Stripe
          window.location.href = checkoutResponse.data.url;
        } catch (paymentError) {
          console.error("Payment initiation failed:", paymentError);
          toast.error("Pedido creado, pero hubo un error al iniciar el pago. Intenta pagar desde el detalle del pedido.");
          setIsLoading(false);
          // Navigate to order page anyway so they don't duplicate order
          navigate(`/order/${order.order_id}`, { replace: true });
        }
      } else {
        // Cash payment
        toast.success("¡Pedido realizado! Paga al repartidor cuando llegue");
        setIsLoading(false);
        navigate(`/order/${order.order_id}`, { replace: true });
      }

    } catch (error) {
      console.error("Error creating order:", error);
      const errorMessage = error.response?.data?.detail || "Error al realizar el pedido";
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Tu carrito está vacío</p>
          <Button
            className="bg-primary text-black hover:opacity-90"
            style={{ backgroundColor: 'var(--primary)' }}
            onClick={() => navigate("/menu")}
          >
            Ver menú
          </Button>
        </div>
      </div>
    );
  }

  // Show restaurant closed message
  if (!isCheckingStatus && !restaurantStatus.is_open) {
    return (
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="sticky top-0 bg-black/95 backdrop-blur z-30 px-4 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/cart")}
              className="text-gray-400 hover:text-primary"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold text-red-500">
              RESTAURANTE CERRADO
            </h1>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center p-8 mt-12">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
            <Clock className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Lo sentimos</h2>
          <p className="text-gray-400 text-center mb-4" data-testid="checkout-closed-message">
            {restaurantStatus.message || "El restaurante está cerrado ahora mismo"}
          </p>
          <p className="text-gray-500 text-sm text-center mb-6">
            Tu carrito se ha guardado. Vuelve cuando estemos abiertos.
          </p>
          <div className="mt-4 space-y-3 w-full max-w-xs">
            <Button
              className="w-full bg-primary text-black hover:opacity-90"
              style={{ backgroundColor: 'var(--primary)' }}
              onClick={() => navigate("/menu")}
            >
              Explorar el menú
            </Button>
            <Button
              variant="outline"
              className="w-full border-gray-700 text-gray-300"
              onClick={() => navigate("/")}
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-40">
      {/* Header */}
      <div className="sticky top-0 bg-black/95 backdrop-blur z-30 px-4 py-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/order-type")}
            className="text-gray-400 hover:text-primary"
            data-testid="checkout-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-bold text-primary" style={{ color: 'var(--primary)' }}>FINALIZAR PEDIDO</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Order Type Badge */}
        <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-900 border border-gray-800">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPickupOrder ? "bg-primary/20" : isLocalOrder ? "bg-green-500/20" : "bg-blue-500/20"
            }`} style={isPickupOrder ? { backgroundColor: 'rgba(var(--primary-rgb), 0.2)' } : {}}>
            {isPickupOrder ? (
              <ShoppingBag className="w-6 h-6 text-primary" style={{ color: 'var(--primary)' }} />
            ) : isLocalOrder ? (
              <UtensilsCrossed className="w-6 h-6 text-green-500" />
            ) : (
              <Truck className="w-6 h-6 text-blue-500" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-white">
                {isPickupOrder ? "RECOGER EN TIENDA" : isLocalOrder ? "PEDIDO EN LOCAL" : "ENVÍO A DOMICILIO"}
              </span>
              <Badge className={isPickupOrder ? "bg-primary text-black" : isLocalOrder ? "bg-green-500 text-black" : "bg-blue-500 text-white"} style={isPickupOrder ? { backgroundColor: 'var(--primary)' } : {}}>
                {isPickupOrder ? "🛍️ PICKUP" : isLocalOrder ? "🍽️ LOCAL" : "🛵 DELIVERY"}
              </Badge>
              {isFromQR && (
                <Badge className="bg-primary text-black" style={{ backgroundColor: 'var(--primary)' }}>
                  📱 QR
                </Badge>
              )}
            </div>
            {isLocalOrder && tableNumber && !isPickupOrder && (
              <p className="text-green-400 text-sm flex items-center gap-1">
                <Table2 className="w-4 h-4" />
                Mesa {tableNumber}
              </p>
            )}
            {isPickupOrder && (
              <p className="text-primary text-sm" style={{ color: 'var(--primary)' }}>
                Te avisaremos cuando esté listo para recoger
              </p>
            )}
          </div>
          {!isFromQR && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/order-type")}
              className="text-gray-400 hover:text-primary"
            >
              Cambiar
            </Button>
          )}
        </div>

        {/* Branch Selector - ALWAYS SHOW if branches exist */}
        {console.log("🔍 [CheckoutPage RENDER] branches.length:", branches.length, "selectedBranch:", selectedBranch?.name)}
        {branches.length > 0 && (
          <section>
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2 text-white">
              <Building className="w-5 h-5 text-primary" style={{ color: 'var(--primary)' }} />
              {branches.length === 1 ? "SUCURSAL" : "SELECCIONA SUCURSAL"}
            </h2>
            {branches.length === 1 && selectedBranch && (
              <div className="text-sm text-gray-400 mb-3">
                ✅ Sucursal seleccionada automáticamente
              </div>
            )}
            <div className="space-y-3">
              {branches.map((branch) => (
                <div
                  key={branch.branch_id}
                  onClick={() => setSelectedBranch(branch)}
                  className={`p-4 rounded-xl border cursor-pointer transition-colors ${selectedBranch?.branch_id === branch.branch_id
                    ? "border-primary bg-primary/10"
                    : "border-gray-700 bg-gray-900 hover:border-gray-600"
                    }`}
                  style={selectedBranch?.branch_id === branch.branch_id ? { borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' } : {}}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{branch.name}</p>
                      <p className="text-sm text-gray-400">{branch.address}</p>
                    </div>
                    {selectedBranch?.branch_id === branch.branch_id && (
                      <div className="w-4 h-4 rounded-full bg-primary" style={{ backgroundColor: 'var(--primary)' }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Delivery Address - Only for delivery orders */}
        {!isLocalOrder && (
          <section>
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2 text-white">
              <MapPin className="w-5 h-5 text-blue-500" />
              DIRECCIÓN DE ENTREGA
            </h2>

            {addresses.length > 0 && !showNewAddress && (
              <div className="space-y-2 mb-4">
                {addresses.map((address) => (
                  <button
                    key={address.address_id}
                    onClick={() => setSelectedAddress(address)}
                    data-testid={`address-${address.address_id}`}
                    className={`w-full p-4 rounded-xl border text-left transition-colors ${selectedAddress?.address_id === address.address_id
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-gray-700 bg-gray-900 hover:border-gray-600"
                      }`}
                  >
                    <p className="font-medium text-white">{address.label}</p>
                    <p className="text-sm text-gray-400">
                      {address.street}, {address.postal_code} {address.city}
                    </p>
                  </button>
                ))}
              </div>
            )}

            {showNewAddress && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 space-y-4 mb-4">
                <div>
                  <Label className="text-gray-300">Etiqueta</Label>
                  <Input
                    value={newAddress.label}
                    onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                    placeholder="Casa, Trabajo, etc."
                    className="bg-gray-800 border-gray-700 text-white"
                    data-testid="new-address-label"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Dirección</Label>
                  <Input
                    value={newAddress.street}
                    onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                    placeholder="Calle, número, piso"
                    className="bg-gray-800 border-gray-700 text-white"
                    data-testid="new-address-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Código postal</Label>
                    <Input
                      value={newAddress.postal_code}
                      onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                      placeholder="04001"
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="new-address-postal"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Ciudad</Label>
                    <Input
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                      placeholder="Almería"
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="new-address-city"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-300"
                    onClick={() => setShowNewAddress(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-yellow-500 text-black hover:bg-yellow-400"
                    onClick={handleSaveAddress}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            )}

            {!showNewAddress && (
              <Button
                variant="outline"
                className="w-full border-gray-700 text-gray-300 hover:text-yellow-500 hover:border-yellow-500"
                onClick={() => setShowNewAddress(true)}
                data-testid="add-new-address-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Añadir nueva dirección
              </Button>
            )}
          </section>
        )}

        {/* Payment Method */}
        <section>
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2 text-white">
            <CreditCard className="w-5 h-5 text-yellow-500" />
            {isLocalOrder ? "¿CÓMO VAS A PAGAR?" : "MÉTODO DE PAGO"}
          </h2>

          {isPickupOrder && (
            <p className="text-gray-400 text-sm mb-4 bg-gray-900/50 p-3 rounded-lg border border-gray-800">
              💡 Pagarás cuando recojas tu pedido en el local. Selecciona cómo prefieres pagar.
            </p>
          )}

          {isLocalOrder && !isPickupOrder && (
            <p className="text-gray-400 text-sm mb-4 bg-gray-900/50 p-3 rounded-lg border border-gray-800">
              💡 Pagarás cuando llegues al local. Selecciona cómo prefieres pagar.
            </p>
          )}

          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
            <label
              className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === "card" ? "border-yellow-500 bg-yellow-500/10" : "border-gray-700 bg-gray-900"
                }`}
            >
              <RadioGroupItem value="card" data-testid="payment-card" className="border-gray-500" />
              <CreditCard className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="font-medium text-white">Tarjeta de crédito/débito</p>
                <p className="text-sm text-gray-400">
                  {isPickupOrder ? "Paga con tarjeta al recoger" : isLocalOrder ? "Paga con tarjeta en caja al retirar" : "Pago seguro con Stripe"}
                </p>
              </div>
            </label>

            <label
              className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors mt-2 ${paymentMethod === "cash" ? "border-yellow-500 bg-yellow-500/10" : "border-gray-700 bg-gray-900"
                }`}
            >
              <RadioGroupItem value="cash" data-testid="payment-cash" className="border-gray-500" />
              <Banknote className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-white">Efectivo</p>
                <p className="text-sm text-gray-400">
                  {isPickupOrder ? "Paga en efectivo al recoger" : isLocalOrder ? "Paga en efectivo en caja al retirar" : "Paga al repartidor"}
                </p>
              </div>
            </label>
          </RadioGroup>
        </section>

        {/* Order Notes */}
        <section>
          <h2 className="font-heading font-bold text-lg mb-4 text-white">
            NOTAS DEL PEDIDO (opcional)
          </h2>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Instrucciones especiales para el pedido..."
            data-testid="checkout-notes"
            className="rounded-xl bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
            rows={3}
          />
        </section>

        {/* Order Summary */}
        <section className="bg-gray-900 rounded-xl border border-gray-800 p-4">
          <h2 className="font-heading font-bold text-lg mb-4 text-white">RESUMEN</h2>

          <div className="space-y-2 text-sm">
            {items.map((item, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-gray-400">
                  {item.quantity}x {item.name}
                </span>
                <span className="text-white">
                  {((item.price + (item.extras?.reduce((s, e) => s + e.price, 0) || 0)) * item.quantity).toFixed(2)} €
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-700 mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Subtotal</span>
              <span className="text-white">{subtotal.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">IVA ({(taxRate * 100).toFixed(0)}%)</span>
              <span className="text-white">{tax.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Envío</span>
              <span className={deliveryFee === 0 ? "text-green-500" : "text-white"}>
                {deliveryFee === 0 ? (
                  <span className="flex items-center gap-1">
                    {originalDeliveryFee > 0 && (
                      <span className="line-through text-gray-500 text-xs">{originalDeliveryFee.toFixed(2)}€</span>
                    )}
                    Gratis
                  </span>
                ) : (
                  `${deliveryFee.toFixed(2)} €`
                )}
              </span>
            </div>

            {/* Discount Section */}
            {hasDiscounts && discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-500 flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Descuento
                </span>
                <span className="text-green-500">-{discount.toFixed(2)} €</span>
              </div>
            )}

            {/* Discount Descriptions */}
            {hasDiscounts && discountDescriptions.length > 0 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-4 h-4 text-green-500" />
                  <span className="text-green-500 font-medium text-sm">¡Ofertas aplicadas!</span>
                </div>
                <div className="space-y-1">
                  {discountDescriptions.map((desc, idx) => (
                    <p key={idx} className="text-xs text-green-400">{desc}</p>
                  ))}
                </div>
                {savings > 0 && (
                  <p className="text-green-500 font-bold text-sm mt-2">
                    Ahorras: {savings.toFixed(2)} €
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-700">
              <span className="text-white">Total</span>
              <span className="text-yellow-500">{total.toFixed(2)} €</span>
            </div>
          </div>
        </section>
      </div>

      {/* Place Order Button */}
      <div className="fixed bottom-20 left-0 right-0 bg-black/95 backdrop-blur border-t border-gray-800 p-4 z-40">
        <Button
          className="w-full h-14 rounded-full font-bold text-lg bg-yellow-500 hover:bg-yellow-400 text-black"
          onClick={handlePlaceOrder}
          disabled={isLoading}
          data-testid="place-order-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Procesando...
            </>
          ) : isLocalOrder ? (
            <>Confirmar pedido · {total.toFixed(2)} €</>
          ) : paymentMethod === "card" ? (
            <>Pagar ahora · {total.toFixed(2)} €</>
          ) : (
            <>Confirmar pedido · {total.toFixed(2)} €</>
          )}
        </Button>
      </div>
    </div>
  );
}
